import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Grid, Icon, Message, Table } from "semantic-ui-react";
import {
	getCoreRowModel,
	PaginationState,
	Row,
	RowSelectionState,
	SortingState,
	useReactTable,
} from "@tanstack/react-table";
import { closestCenter, DndContext } from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { horizontalListSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { useVirtualizer, VirtualItem, Virtualizer } from "@tanstack/react-virtual";
import usePropertyAccessors from "../../../hooks/usePropertyAccessors";
import Preloader from "../Preloader/Preloader";
import DataTableContext, { IDataTableContext } from "../../../contexts/DataTableContext";
import SelectColumnsModal from "./SelectColumnsModal";
import useSelectedColumns from "../../../hooks/useSelectedColumns";
import useTableColumns from "../../../hooks/useTableColumns";
import "./DataTable.scss";
import useTeamProperties from "../../../hooks/useTeamProperties";
import useDraggableColumns, { persistentColumns } from "../../../hooks/useDraggableColumns";
import DraggableTableHeader from "./DraggableTableHeader";
import DragAlongCell from "./DragAlongCell";
import DataIntegrityKey from "./DataIntegrityKey";
import { Accessor, Batch, Filter, Group, PropertyAttribute, PropertyFilter, QueryFilter } from "../../../types/types";
import useQueryFromState from "../../../hooks/useQueryFromState";
import Pagination from "../Pagination/Pagination";
import useStateFromQuery from "../../../hooks/useStateFromQuery";
import { Property } from "../../../rest/responses/Property";
import Filters from "./Filters";
import usePropertyAggregates from "../../../hooks/usePropertyAggregates";
import usePropertyFilters from "../../../hooks/usePropertyFilters";
import Button from "../Button/Button";
import ExportButton from "../ExportButton/ExportButton";
import { useRestClientContext } from "../../../contexts/RestClientContext";
import { useTeamContext } from "../../../contexts/TeamContext";
import GroupPropertiesModal from "../../Modal/GroupPropertiesModal";
import { useParams } from "react-router-dom";
import ConfirmationModal from "../../Modal/ConfirmationModal";
import useBulkEditModal from "../../../hooks/useBulkEditModal";
import CustomImprovementSetsModal from "../../CustomImprovements/CustomImprovementSetsModal";
import BulkCustomImprovementSetsProgressModal from "../../CustomImprovements/CustomImprovementSetsProgressModal";
import VirtualizedRow from "./VirtualizedRow";
import VirtualizedBodyRow from "./VirtualizedBodyRow";
import QuickSearch from "./QuickSearch";
import GroupSelect from "../GroupSelect/GroupSelect";
import _ from "lodash";
import { useDummyScrollbar } from "../../../hooks/useDummyScrollbar";

export interface DataTableState {
	filters?: (Filter | PropertyFilter)[];
	pagination?: PaginationState;
	sorting?: SortingState;
	quickSearch?: string;
	queryId?: string;
}

interface DataTableProps {
	editable?: boolean;
	displayIntegrity?: boolean;
}

const DEFAULT_ROW_HEIGHT = 40;
const IMAGE_ROW_HEIGHT = 180;

function getDefaultRowHeight(row: Row<Property>): number {
	const imageUrls = row.original["image_meta.image_urls"];
	if (Array.isArray(imageUrls) && imageUrls.length > 0) {
		return IMAGE_ROW_HEIGHT;
	}

	return DEFAULT_ROW_HEIGHT;
}

export default function DataTable({ editable, displayIntegrity }: DataTableProps) {
	// NB: REST client and team
	const client = useRestClientContext();
	const team = useTeamContext();
	const route = useParams();

	// NB: Columns and accessors
	const { loading: isLoadingAccessors, ...forwardFromUsePropertyAccessors } = usePropertyAccessors();
	const forwardFromUseSelectedAccessors = useSelectedColumns();
	const { selectedAccessors, selectedPropertyAttributes, setSelectedAccessors, setSelectedPropertyAttributes } =
		forwardFromUseSelectedAccessors;

	// Merged into the property query only
	const forcedAccessors: Accessor[] = displayIntegrity ? ["data_integrity.integrity"] : [];

	const columns = useTableColumns(selectedAccessors, selectedPropertyAttributes);
	const { columnOrder, setColumnOrder, sensors, onDragEnd } = useDraggableColumns({
		columns,
		onColumnOrderChange: (newArrangement) => saveArrangement(newArrangement),
	});
	const [isSelectColumnsModalOpen, setIsSelectColumnsModalOpen] = useState(false);
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
	const [isGroupPropertiesModalOpen, setIsGroupPropertiesModalOpen] = useState(false);
	const [groupSelectRefresh, setGroupSelectRefresh] = useState(0);
	const [isUngroupPropertiesConfirmationOpen, setIsUngroupPropertiesConfirmationOpen] = useState<boolean>(false);
	const [isUngroupingProperties, setIsUngroupingProperties] = useState<boolean>(false);
	const [customImprovementSetsModalOpen, setCustomImprovementSetsModalOpen] = useState(false);
	const [currentBulkCustomImprovementSetsBatches, setCurrentBulkCustomImprovementSetsBatches] = useState<Batch[]>();
	const [isConfirmDeletePropertiesModalOpen, setIsConfirmDeletePropertiesModalOpen] = useState(false);
	const [isDeletingProperties, setIsDeletingProperties] = useState(false);

	// NB: Filters
	const filtering = usePropertyFilters();

	// Initialise the group IDs from URL on load.
	const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>(route.group_id ? [Number(route.group_id)] : []);
	// Convert the group IDs into a filter. This is separate from usePropertyFilters as that is only related to the
	// filters in the filter modal.
	const groupFilter: QueryFilter | undefined =
		selectedGroupIds.length > 0 ? { filter: "group_ids", term: selectedGroupIds } : undefined;
	const combinedFilters = groupFilter ? [...filtering.filters, groupFilter] : filtering.filters;

	// NB: Aggregates
	const { loading: isLoadingAggregates, ...forwardFromUsePropertyAggregates } = usePropertyAggregates();

	// NB: Pagination
	const initialTableState = useStateFromQuery<{
		pagination: PaginationState;
		sorting: SortingState;
		quickSearch?: string;
	}>();

	const [quickSearch, setQuickSearch] = useState<string>(
		initialTableState && "quickSearch" in initialTableState && initialTableState.quickSearch
			? initialTableState.quickSearch
			: ""
	);

	// NB: Row selection
	const selectedIds = Object.keys(rowSelection).map((value) => Number(value));
	const numSelectedRows = selectedIds.length;

	// NB: Top scrollbar
	const { component: dummyScrollbar, scrollerRef } = useDummyScrollbar();

	// NB: The actual table
	const [tableDataProps, setTableDataProps] = useState<{
		data: Property[];
		rowCount: number;
	}>({
		data: [],
		rowCount: 0,
	});

	const table = useReactTable({
		...tableDataProps,
		getRowId: (row) => row.id.toString(),
		columns,
		getCoreRowModel: getCoreRowModel(),
		manualPagination: true,
		manualSorting: true,
		initialState: initialTableState,
		enableColumnResizing: true,
		columnResizeMode: "onChange",
		state: {
			columnOrder,
			rowSelection,
		},
		onColumnOrderChange: setColumnOrder,
		onRowSelectionChange: setRowSelection,
	});

	// NB: Column virtualization

	const visibleColumns = table.getVisibleLeafColumns();
	const columnVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableCellElement>({
		count: visibleColumns.length,
		estimateSize: (index) => visibleColumns[index].getSize(),
		getItemKey: (index) => visibleColumns[index]?.id ?? index,
		getScrollElement: () => scrollerRef.current,
		horizontal: true,
		overscan: 3, // NB: how many columns to render on each side off screen each way (adjust this for performance)
	});

	// Trigger column remeasure on new columns come in that may have differing widths.
	const columnLayoutSignature = visibleColumns.map((col) => `${col.id}:${col.getSize()}`).join("|");
	useLayoutEffect(() => {
		columnVirtualizer.measure();
	}, [columnVirtualizer, columnLayoutSignature]);

	const virtualColumns = columnVirtualizer.getVirtualItems();
	let virtualPaddingLeft: number | undefined;
	let virtualPaddingRight: number | undefined;

	if (columnVirtualizer && virtualColumns?.length) {
		virtualPaddingLeft = virtualColumns[0]?.start ?? 0;
		virtualPaddingRight = columnVirtualizer.getTotalSize() - (virtualColumns[virtualColumns.length - 1]?.end ?? 0);
	}

	// NB: Body row virtualization
	const { rows } = table.getRowModel();
	// Hold the current row heights so that they do not shrink.
	const rowHeightsRef = useRef<Map<string, number>>(new Map());
	// Hold row virtualiser also in a ref otherwise we'll get a circular reference.
	const rowVirtualizerRef = useRef<Virtualizer<HTMLDivElement, HTMLTableRowElement> | null>(null);

	const persistRowHeight = useCallback(
		(rowId: string, measuredHeight: number): number => {
			const rowIndex = rows.findIndex((row) => row.id === rowId);
			if (rowIndex === -1) return measuredHeight;

			const defaultHeight = getDefaultRowHeight(rows[rowIndex]);
			const storedHeight = rowHeightsRef.current.get(rowId) ?? defaultHeight;
			const nextHeight = Math.max(storedHeight, measuredHeight);

			if (nextHeight > storedHeight) {
				rowHeightsRef.current.set(rowId, nextHeight);
				rowVirtualizerRef.current?.resizeItem(rowIndex, nextHeight);
			}

			return rowHeightsRef.current.get(rowId) ?? defaultHeight;
		},
		[rows]
	);

	const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
		count: rows.length,
		getItemKey: (index) => rows[index].id,
		estimateSize: (index) => rowHeightsRef.current.get(rows[index].id) ?? getDefaultRowHeight(rows[index]),
		getScrollElement: () => scrollerRef.current,
		// measure dynamic row height, except in firefox because it measures table border height incorrectly
		measureElement:
			typeof window !== "undefined" && navigator.userAgent.indexOf("Firefox") === -1
				? (element) => {
						if (!element) return DEFAULT_ROW_HEIGHT;

						const index = Number(element.getAttribute("data-index"));
						const row = rows[index];
						if (!row) return DEFAULT_ROW_HEIGHT;

						return persistRowHeight(row.id, element.getBoundingClientRect().height);
					}
				: undefined,
		overscan: 5,
	});
	rowVirtualizerRef.current = rowVirtualizer;

	const virtualRows = rowVirtualizer.getVirtualItems();

	// NB: Sorting and pagination
	const { sorting, pagination } = table.getState();

	// NB: Data from the API
	const {
		properties,
		loading: isLoadingProperties,
		refresh,
		error: propertiesError,
		queryId,
		saveArrangement,
	} = useTeamProperties({
		loading: isLoadingAccessors,
		// Merge arrays without duplication
		selectedAccessors: _.union(selectedAccessors, forcedAccessors),
		selectedPropertyAttributes,
		sorting,
		pagination,
		filters: combinedFilters,
		groupId: selectedGroupIds.length === 1 ? selectedGroupIds[0] : undefined,
		quickSearch,
		setColumnOrder,
		setSelectedAccessors,
		setSelectedPropertyAttributes,
		accessorPrefix: forcedAccessors,
	});

	useEffect(() => {
		if (!properties) return;

		setTableDataProps({
			data: properties.entries,
			rowCount: properties.total_entries,
		});
	}, [properties]);

	// NB: Persist state in query string
	useQueryFromState({
		pagination,
		sorting,
		filters: filtering.filters,
		quickSearch,
		queryId,
	});

	// NB: Bulk editing
	const { open: onOpenBulkEditModal, component: bulkEditModal } = useBulkEditModal({
		selectedIds,
		typesByAccessor: forwardFromUsePropertyAccessors.typesByAccessor,
		refresh,
	});

	// NB: Event listeners
	const onOpenSelectColumnsModal = () => setIsSelectColumnsModalOpen(true);

	const onExport = () => {
		const orderedAccessors = columnOrder.filter((accessor) => !persistentColumns.includes(accessor)) as (
			| Accessor
			| PropertyAttribute
		)[];
		if (selectedIds.length)
			return client.v3.createPropertyExport(
				team,
				selectedAccessors,
				selectedPropertyAttributes,
				{
					ids: selectedIds,
				},
				orderedAccessors
			);
		else if (filtering.filters.length)
			return client.v3.createPropertyExport(
				team,
				selectedAccessors,
				selectedPropertyAttributes,
				{
					filters: filtering.filters,
				},
				orderedAccessors
			);
		else
			return client.v3.createPropertyExport(
				team,
				selectedAccessors,
				selectedPropertyAttributes,
				orderedAccessors
			);
	};

	const onGroupSelection = () => setIsGroupPropertiesModalOpen(true);
	const onConfirmUngroup = () => {
		const groupId = Number(route.group_id);

		if (!route.group_id || Number.isNaN(groupId)) {
			throw new Error("Group ID should've been set as a number");
		}

		setIsUngroupingProperties(true);
		const body = {
			properties: selectedIds,
		};

		client.deleteGroupProperties(team.id, Number(groupId), body).then(() => {
			setIsUngroupingProperties(false);
			setRowSelection({});
			setIsUngroupPropertiesConfirmationOpen(false);
			refresh();
		});
	};
	const onCloseGroupProperties = () => setIsGroupPropertiesModalOpen(false);

	const onRunCustomImprovements = () => {
		setCustomImprovementSetsModalOpen(true);
	};

	const onBulkCustomImprovementSetsStarted = (batches: Batch[]) => {
		setCurrentBulkCustomImprovementSetsBatches(batches);
	};

	const onDeleteSelection = () => setIsConfirmDeletePropertiesModalOpen(true);
	const onConfirmDeleteProperties = () => {
		setIsDeletingProperties(true);

		client.deleteTeamProperties(team.id, selectedIds).then(() => {
			setIsDeletingProperties(false);
			setIsConfirmDeletePropertiesModalOpen(false);
			setRowSelection({});
			refresh();
		});
	};
	const onCloseConfirmDeleteProperties = () => setIsConfirmDeletePropertiesModalOpen(false);

	// NB: Components
	if (isLoadingAccessors) return <Preloader />;

	let exportNoun: string;

	if (numSelectedRows > 0) exportNoun = "selected";
	else if (filtering.filters.length > 0) exportNoun = "filtered";
	else exportNoun = "all";

	let contextProps: IDataTableContext = {
		...forwardFromUsePropertyAccessors,
		...forwardFromUseSelectedAccessors,
		...forwardFromUsePropertyAggregates,
		isLoadingAggregates,
		isSelectColumnsModalOpen,
		setIsSelectColumnsModalOpen,
		selectedIds,
		displayIntegrity,
		table,
	};

	if (editable) contextProps.onBulkEdit = onOpenBulkEditModal;

	return (
		<DataTableContext.Provider value={contextProps}>
			{propertiesError && propertiesError.message && <Message error>{propertiesError.message}</Message>}
			<div className="data-table-wrapper">
				<Grid className="selection-controls">
					<ExportButton initiate={onExport} text={`Export ${exportNoun}`} />
					<Button variant="secondary" disabled={numSelectedRows === 0} onClick={onGroupSelection}>
						<Icon name="folder outline" />
						Group selection
					</Button>
					<Button variant="secondary" disabled={numSelectedRows === 0} onClick={onRunCustomImprovements}>
						<Icon name="play" />
						Run Custom Measures
					</Button>
					{route.group_id && (
						<Button
							variant="danger"
							negative
							disabled={numSelectedRows === 0}
							onClick={() => setIsUngroupPropertiesConfirmationOpen(true)}
						>
							<Icon name="remove" />
							Ungroup selection
						</Button>
					)}
					<Button variant="danger" negative disabled={numSelectedRows === 0} onClick={onDeleteSelection}>
						<Icon name="erase" />
						Delete selection
					</Button>
				</Grid>
				<div className={"second-level-container"}>
					<div className={"filter-bar-container"}>
						<Filters {...filtering} />
						<GroupSelect
							className={"group-filter"}
							multiple
							placeholder="Filter by group..."
							currentlySelected={selectedGroupIds}
							refresh={groupSelectRefresh}
							onChange={(groups: Group[]) => setSelectedGroupIds(groups.map((group) => group.id))}
						/>
						<QuickSearch
							setState={setQuickSearch}
							loading={isLoadingProperties}
							initialValue={quickSearch}
						/>
					</div>
					{/* Floats right */}
					{displayIntegrity && <DataIntegrityKey />}
				</div>

				{dummyScrollbar}
				<div className="data-table-container">
					<div className="data-table-scroller" ref={scrollerRef}>
						<DndContext
							collisionDetection={closestCenter}
							modifiers={[restrictToHorizontalAxis]}
							onDragEnd={onDragEnd}
							sensors={sensors}
						>
							<Table
								className={`data-table${displayIntegrity ? " has-integrity" : ""}`}
								data-loading={isLoadingProperties}
								style={{ display: "grid" }}
							>
								<Table.Header
									// Make the column header sticky so that it shows when the table has been scrolled
									style={{
										display: "grid",
										position: "sticky",
										top: 0,
										zIndex: 1,
										minHeight: "50px",
									}}
								>
									{table.getHeaderGroups().map((headerGroup) => (
										<VirtualizedRow
											key={headerGroup.id}
											columnVirtualizer={columnVirtualizer}
											virtualPaddingLeft={virtualPaddingLeft}
											virtualPaddingRight={virtualPaddingRight}
										>
											{(items: VirtualItem[]) => (
												<SortableContext
													items={columnOrder}
													strategy={horizontalListSortingStrategy}
												>
													{items.map((virtualColumn) => {
														const header = headerGroup.headers[virtualColumn.index];
														return <DraggableTableHeader key={header.id} header={header} />;
													})}
												</SortableContext>
											)}
										</VirtualizedRow>
									))}
								</Table.Header>
								<Table.Body
									style={{
										display: "grid",
										height: `${rowVirtualizer.getTotalSize()}px`, // NB: Tells scrollbar how big the table is
										width: `${columnVirtualizer.getTotalSize()}px`,
										position: "relative", // NB: Needed for absolute positioning of rows
									}}
								>
									{virtualRows.map((virtualRow) => {
										const row = rows[virtualRow.index];

										return (
											<VirtualizedBodyRow
												columnVirtualizer={columnVirtualizer}
												key={row.id}
												onRowHeightChange={persistRowHeight}
												row={row}
												rowHeight={
													rowHeightsRef.current.get(row.id) ?? getDefaultRowHeight(row)
												}
												rowVirtualizer={rowVirtualizer}
												virtualPaddingLeft={virtualPaddingLeft}
												virtualPaddingRight={virtualPaddingRight}
												virtualRow={virtualRow}
											>
												{(virtualColumns, visibleCells) =>
													virtualColumns.map((virtualColumn) => {
														const cell = visibleCells[virtualColumn.index];

														return (
															<SortableContext
																key={cell.id}
																items={columnOrder}
																strategy={horizontalListSortingStrategy}
															>
																<DragAlongCell key={cell.id} cell={cell} />
															</SortableContext>
														);
													})
												}
											</VirtualizedBodyRow>
										);
									})}
								</Table.Body>
							</Table>
						</DndContext>
					</div>
					{isLoadingProperties && <Preloader variant="cover" />}
					<Button
						variant="contained tiny"
						className="column-select"
						title="Select columns"
						onClick={onOpenSelectColumnsModal}
					>
						<Icon name="columns" />
					</Button>
				</div>
				{numSelectedRows > 0 && (
					<div>
						<strong>{numSelectedRows} properties</strong> selected
					</div>
				)}
				{properties && (
					<Pagination
						numResults={properties.total_entries}
						numPages={properties.total_pages}
						currentPage={table.getState().pagination.pageIndex + 1}
						pageSize={table.getState().pagination.pageSize}
						canPreviousPage={table.getCanPreviousPage()}
						canNextPage={table.getCanNextPage()}
						onPreviousPage={() => table.previousPage()}
						onNextPage={() => table.nextPage()}
						onFirstPage={() => table.firstPage()}
						onLastPage={() => table.lastPage()}
						onJumpToPage={(page: number) => table.setPageIndex(page)}
						onChangePageSize={(size: number) => table.setPageSize(size)}
					/>
				)}
				<SelectColumnsModal />
				{isGroupPropertiesModalOpen && (
					<GroupPropertiesModal
						onClose={onCloseGroupProperties}
						onGroupCreated={() => setGroupSelectRefresh((key) => key + 1)}
					/>
				)}
				{customImprovementSetsModalOpen && (
					<CustomImprovementSetsModal
						teamId={team.id}
						data={{
							properties: selectedIds,
						}}
						onClose={() => setCustomImprovementSetsModalOpen(false)}
						onComplete={(batches: Batch[]) => onBulkCustomImprovementSetsStarted(batches)}
					/>
				)}
				{currentBulkCustomImprovementSetsBatches && (
					<BulkCustomImprovementSetsProgressModal
						batches={currentBulkCustomImprovementSetsBatches.map((batch) => {
							// TODO: Review please.. bit hacky
							return Object.assign(
								{
									options: {
										team_id: team.id,
									},
								},
								batch
							);
						})}
						onClose={() => setCurrentBulkCustomImprovementSetsBatches(undefined)}
					/>
				)}
				{bulkEditModal}
				<ConfirmationModal
					title="Delete properties"
					open={isConfirmDeletePropertiesModalOpen}
					buttonsDisabled={isDeletingProperties}
					onConfirm={onConfirmDeleteProperties}
					onCancel={onCloseConfirmDeleteProperties}
				>
					<p>Are you sure you want to delete {selectedIds.length} properties?</p>
					<p>This action is not reversible.</p>
				</ConfirmationModal>
				<ConfirmationModal
					title={`Remove ${selectedIds.length} properties from group?`}
					open={isUngroupPropertiesConfirmationOpen}
					buttonsDisabled={isUngroupingProperties}
					onConfirm={onConfirmUngroup}
					onCancel={() => setIsUngroupPropertiesConfirmationOpen(false)}
					confirmationText={"Ungroup"}
				/>
			</div>
		</DataTableContext.Provider>
	);
}
